import { create } from "zustand";
import {
  BufferStocksInput,
  StoreStock,
  StoreStocksQuery,
  StoreStocksState,
} from "../types/store_stock";
import { useFetcher } from "./fetcher";
import { Paging } from "../types/paging";
import { sortQueryFromOrderByRecord } from "../utils/query";
import { SyncToOnlineChannelsPayload } from "../types/stock_sync";

export const useStoreStocksStore = create<StoreStocksState>((set, get) => ({
  items: [],
  total: null,
  query: null,
  limit: 20,
  showFilter: (() => { try { const v = localStorage.getItem('showFilter:stocks'); return v !== null ? JSON.parse(v) : true; } catch { return true; } })(),
  search: async (query?: StoreStocksQuery, fetch?: boolean) => {
    set(() => ({ query }));
    if (fetch !== false) {
      await get().fetch(true);
    }
  },
  fetch: async (reset: boolean): Promise<Paging<StoreStock> | null> => {
    try {
      const { items, limit, query, total } = get();
      if (!reset && total !== null && total <= items.length) {
        return null;
      }

      const sort = sortQueryFromOrderByRecord(query?.order_by);
      const params: any = query
        ? {
            ...query,
            order_by: undefined,
            sort,
            limit,
            offset: reset ? 0 : items.length,
          }
        : { limit, offset: reset ? 0 : items.length };
      const res = await useFetcher
        .getState()
        .omsDsFetcher.get<Paging<StoreStock>>("store-stocks", {
          params,
          paramsSerializer: {
            indexes: null,
          },
        });

      set(() => ({
        items: [...(reset ? [] : items), ...(res?.data?.items ?? [])],
        total: res?.data?.total ?? null,
      }));
      return res?.data ?? null;
    } catch (error) {
      throw error;
    }
  },
  list: async (input?: StoreStocksQuery): Promise<Paging<StoreStock>> => {
    try {
      const { limit, offset, order_by, ...query } = input
        ? input
        : { limit: 20, offset: 0, order_by: undefined };
      const sortQuery = sortQueryFromOrderByRecord(order_by);
      const params: any = query
        ? {
            ...query,
            limit: limit ? limit : 20,
            offset: offset ? offset : 0,
            sort: sortQuery,
          }
        : {
            limit: limit ? limit : 20,
            offset: offset ? offset : 0,
            sort: sortQuery,
          };
      const res = await useFetcher
        .getState()
        .omsDsFetcher.get<Paging<StoreStock>>("store-stocks", {
          params,
          paramsSerializer: {
            indexes: null,
          },
        });
      return res?.data ?? null;
    } catch (error) {
      throw error;
    }
  },
  get: async (storeCode: string): Promise<StoreStock | null> => {
    try {
      const res = await useFetcher
        .getState()
        .omsDsFetcher.get<StoreStock>(`store-stocks/${storeCode}`);
      return res?.data ?? null;
    } catch (error) {
      throw error;
    }
  },
  bufferStocks: async (input: BufferStocksInput): Promise<void> => {
    try {
      await useFetcher
        .getState()
        .omsDsFetcher.post(`store-stocks/${input.store_code}/buffer-stocks`, input);
    } catch (error) {
      throw error;
    }
  },
  syncToChannels: async (input: SyncToOnlineChannelsPayload): Promise<void> => {
    try {
      await useFetcher
        .getState()
        .omsDsFetcher.post(`store-stocks/${input.store_code}/sync-to-channels`, input);
    } catch (error) {
      throw error;
    }
  },
  setShowFilter: async (value: boolean): Promise<void> => {
    set(() => ({
      showFilter: value,
    }));
    try { localStorage.setItem('showFilter:stocks', JSON.stringify(value)); } catch {}
  },
  clear: (): void => {
    set(() => ({ items: [], total: null, query: null }));
  },
}));
